import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Calendar, User, Factory, ArrowRight, ArrowLeft, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Applies head/meta tags for a single article and returns a cleanup function
// that restores everything to whatever it was before this article was viewed.
function applyHead({ title, description, ogImage, canonicalUrl }) {
  const changes = [];

  const prevTitle = document.title;
  document.title = title;
  changes.push(() => { document.title = prevTitle; });

  const ensureMeta = (selector, attrs) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      changes.push(() => el.remove());
      return el;
    }
    const prev = el.getAttribute("content");
    changes.push(() => { if (prev == null) el.removeAttribute("content"); else el.setAttribute("content", prev); });
    return el;
  };

  const set = (selector, attrs, content) => {
    if (content == null) return;
    ensureMeta(selector, attrs).setAttribute("content", content);
  };

  set('meta[name="description"]',        { name: "description" },        description);
  set('meta[property="og:title"]',       { property: "og:title" },       title);
  set('meta[property="og:description"]', { property: "og:description" }, description);
  set('meta[property="og:type"]',        { property: "og:type" },        "article");
  set('meta[name="twitter:title"]',      { name: "twitter:title" },      title);
  set('meta[name="twitter:description"]',{ name: "twitter:description" },description);

  if (canonicalUrl) {
    set('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      canon.setAttribute("href", canonicalUrl);
      document.head.appendChild(canon);
      changes.push(() => canon.remove());
    } else {
      const prev = canon.getAttribute("href");
      canon.setAttribute("href", canonicalUrl);
      changes.push(() => { if (prev == null) canon.removeAttribute("href"); else canon.setAttribute("href", prev); });
    }
  }

  if (ogImage) {
    set('meta[property="og:image"]',   { property: "og:image" },   ogImage);
    set('meta[name="twitter:image"]',  { name: "twitter:image" },  ogImage);
  }

  return () => { changes.reverse().forEach((fn) => { try { fn(); } catch { /* best effort restore */ } }); };
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/public/insights/${slug}`);
      setPost(data);
    } catch (e) {
      console.error("Failed to load insight:", e);
      setError(e?.response?.status === 404 ? "Article not found" : "Failed to load article");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  // Apply SEO head tags whenever the loaded post changes, and restore them
  // when the user navigates away so no stale meta bleeds into other pages.
  useEffect(() => {
    if (!post) return;
    const cleanup = applyHead({
      title: `${post.title} — LumberLoop USA`,
      description: post.meta_description || post.excerpt || "",
      ogImage: post.featured_image_url || `${window.location.origin}/lumberloop-og.jpg`,
      canonicalUrl: `${window.location.origin}/insights/${post.slug}`,
    });
    return cleanup;
  }, [post]);

  if (loading) {
    return <div className="py-32 text-center font-mono-ll text-sm tracking-widest text-slate-500">LOADING…</div>;
  }
  if (error || !post) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-heading text-4xl font-black uppercase text-[#1A365D]">{error || "Not found"}</h1>
        <Link to="/insights" className="btn-primary mt-6 inline-flex">← All insights</Link>
      </div>
    );
  }

  // JSON-LD Article structured data for SEO
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.featured_image_url ? [post.featured_image_url] : undefined,
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Person", name: post.author || "LumberLoop USA Editorial" },
    publisher: {
      "@type": "Organization",
      name: "LumberLoop USA",
      logo: { "@type": "ImageObject", url: `${window.location.origin}/lumberloop-logo.png` },
    },
    mainEntityOfPage: `${window.location.origin}/insights/${post.slug}`,
  };

  return (
    <article data-testid="insight-detail-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(ldJson).replace(/</g, "\\u003c"),
        }}
      />

      {/* HEADER */}
      <header className="relative bg-[#1A365D] text-white overflow-hidden border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-20"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <Link to="/insights" className="font-mono-ll text-[11px] tracking-[0.3em] text-slate-300 hover:text-[#C62828] inline-flex items-center gap-2" data-testid="back-to-insights">
            <ArrowLeft size={14}/> All Insights
          </Link>
          <span className="mt-5 inline-block px-2 py-1 bg-[#C62828] text-white text-[10px] font-mono-ll tracking-widest uppercase">{post.category}</span>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[1.05]" data-testid="insight-title">
            {post.title}
          </h1>
          {post.excerpt && <p className="mt-5 text-lg text-slate-200 max-w-2xl">{post.excerpt}</p>}
          <div className="mt-6 flex flex-wrap gap-4 font-mono-ll text-[11px] tracking-widest uppercase text-slate-300">
            <span className="flex items-center gap-2"><Calendar size={13}/> {fmtDate(post.published_at || post.created_at)}</span>
            {post.author && <span className="flex items-center gap-2"><User size={13}/> {post.author}</span>}
            {post.manufacturer_name && <span className="flex items-center gap-2"><Factory size={13}/> {post.manufacturer_name}</span>}
          </div>
        </div>
        <div className="h-2 stripes-flag"></div>
      </header>

      {/* FEATURED IMAGE */}
      {post.featured_image_url && (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 -mt-2">
          <div className="aspect-[16/8] w-full overflow-hidden border-2 border-[#1A365D]">
            <img src={post.featured_image_url} alt={post.title} className="w-full h-full object-cover" data-testid="insight-hero-image"/>
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="insight-prose" data-testid="insight-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
          >
            {post.body || ""}
          </ReactMarkdown>
        </div>

        {/* RELATED PRODUCTS */}
        {post.related_products && post.related_products.length > 0 && (
          <section className="mt-12 border-t-2 border-[#1A365D] pt-8" data-testid="related-products">
            <div className="label-tag">Featured Products</div>
            <h2 className="font-heading text-2xl md:text-3xl font-black uppercase mt-2 text-[#1A365D]">Mentioned in this article</h2>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {post.related_products.map((p) => (
                <div key={p.id} className="brutal-card-flat p-4 flex items-start gap-3" data-testid={`related-product-${p.id}`}>
                  <Package className="text-[#C62828] mt-1 flex-shrink-0" size={18}/>
                  <div>
                    <div className="font-bold text-[#1A365D]">{p.name}</div>
                    <div className="text-xs font-mono-ll tracking-widest text-slate-500 uppercase">{p.manufacturer_name} · {p.category}</div>
                    <div className="text-sm text-slate-700 mt-1 line-clamp-2">{p.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* INLINE CTA */}
        <section className="mt-12 brutal-card p-8 bg-[#1A365D] text-white">
          <div className="label-tag text-slate-300">Take action</div>
          <h2 className="font-heading text-2xl md:text-4xl font-black uppercase mt-2 leading-tight">
            Get matched with local dealers <span className="text-[#C62828]">in minutes.</span>
          </h2>
          <p className="mt-3 text-slate-200 max-w-xl">
            Submit your material needs and ZIP — we&apos;ll route to the closest qualified dealers in your market.
          </p>
          <Link to="/request-quote" className="btn-primary mt-5 inline-flex items-center gap-2" data-testid="insight-cta-quote">
            Request a Quote <ArrowRight size={16}/>
          </Link>
        </section>
      </div>

      {/* SUGGESTED READS */}
      {post.suggested && post.suggested.length > 0 && (
        <section className="bg-[#EAEAE5] border-t-2 border-[#1A365D]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
            <div className="label-tag">Keep reading</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">More insights</h2>
            <div className="mt-6 grid md:grid-cols-3 gap-5">
              {post.suggested.map((s) => (
                <Link key={s.id} to={`/insights/${s.slug}`} className="block brutal-card-flat overflow-hidden bg-white group hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform" data-testid={`suggested-${s.slug}`}>
                  <div className="aspect-[16/10] bg-slate-200 overflow-hidden">
                    {s.featured_image_url ? (
                      <img src={s.featured_image_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform"/>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1A365D] to-[#C62828]"></div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="px-2 py-1 bg-[#1A365D] text-white text-[10px] font-mono-ll tracking-widest uppercase">{s.category}</span>
                    <h3 className="mt-3 font-heading text-lg font-bold uppercase leading-tight text-[#1A365D] group-hover:text-[#C62828] transition-colors line-clamp-2">{s.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
